/**
 * Iconos SVG. Los trazados son los del maquetado, copiados tal cual — mismo `viewBox`,
 * mismo `stroke-width`, mismos remates.
 *
 * Un único componente con un mapa de trazados en lugar de 24 ficheros: son iconos de línea
 * con la misma geometría y sirven todos con los mismos atributos.
 */

export type IconName =
  | 'home'
  | 'building'
  | 'users'
  | 'transfer'
  | 'euro'
  | 'cash-flow'
  | 'chart'
  | 'wrench'
  | 'file'
  | 'bell'
  | 'chat'
  | 'chevron-down'
  | 'chevron-right'
  | 'chevron-left'
  | 'info'
  | 'plus'
  | 'edit'
  | 'trash'
  | 'download'
  | 'upload'
  | 'send'
  | 'file-lines'
  | 'pin'
  | 'logout'
  | 'eye'
  | 'eye-off'
  | 'check'
  | 'arrow-right'
  | 'lock'
  | 'grid'
  | 'list'
  | 'close'
  | 'search'
  | 'star'
  | 'image'
  | 'drag'
  | 'bolt'
  | 'grip-lines'
  | 'expand'
  | 'filter';

/** `d` de cada trazado, literal del maquetado. */
const PATHS: Record<IconName, string> = {
  home: 'M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z',
  building: 'M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h6',
  users:
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11',
  transfer: 'M4 7h13l-3-3M20 17H7l3 3',
  euro: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  chart: 'M3 3v18h18M8 16V9M13 16V5M18 16v-4',
  /*
   * Ingresos y gastos: una flecha que baja y otra que sube.
   *
   * NO puede ser `chart`, que es el de Informes: dos entradas contiguas de la navegación con
   * el mismo dibujo obligan a leer la etiqueta para distinguirlas, y en el raíl de 72px no hay
   * etiqueta que leer. Verticales y no horizontales para no confundirse con `transfer`, que es
   * el de Transacciones y está tres filas más arriba.
   */
  'cash-flow': 'M7 4v16M4 17l3 3 3-3M17 20V4M14 7l3-3 3 3',
  wrench: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2z',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h6',
  bell: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  chat: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  'chevron-down': 'm6 9 6 6 6-6',
  'chevron-right': 'm9 18 6-6-6-6',
  'chevron-left': 'm15 18-6-6 6-6',
  info: 'M12 16v-4M12 8h.01',
  plus: 'M12 5v14M5 12h14',
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z',
  trash: 'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 9l5-5 5 5M12 4v12',
  send: 'm22 2-7 20-4-9-9-4z',
  'file-lines': 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z',
  pin: 'M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  // --- Fase 2B ------------------------------------------------------------
  eye: 'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z',
  'eye-off':
    'm3 3 18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.5 5.2A10.4 10.4 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.3 4.1M6.4 6.5A17.4 17.4 0 0 0 2 12s3.6 7 10 7c1.4 0 2.7-.3 3.8-.8',
  check: 'm4.5 12.5 5 5L19.5 7',
  'arrow-right': 'M4 12h15M13 6l6 6-6 6',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  list: 'M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01',
  close: 'M6 6l12 12M18 6 6 18',
  search: 'M21 21l-4.2-4.2M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z',
  /* Filtros: tres rayas que se acortan, el embudo sin dibujar un embudo. */
  filter: 'M4 6h16M7 12h10M10 18h4',
  star: 'm12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.2-5.4-3-5.4 3 1-6.2L3.2 9.5l6.1-.9z',
  image: 'M3 5h18v14H3zM3 16l5-5 4 4 3-3 6 6',
  drag: 'M9 5h.01M15 5h.01M9 12h.01M15 12h.01M9 19h.01M15 19h.01',
  /* Rayo: suministros (luz, agua, gas…). */
  bolt: 'M13 2 4 14h7l-1 8 9-12h-7l1-8z',
  /* Asa de arrastre de tres rayas, la convención para «esto se puede mover a otro sitio». */
  'grip-lines': 'M5 7h14M5 12h14M5 17h14',
  /* Ampliar: dos esquinas que se abren, para el mapa que se agranda en un modal. */
  expand: 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7',
};

/** El icono de información lleva además un círculo, que no cabe en un solo `d`. */
const EXTRA_CIRCLE: Partial<Record<IconName, { cx: number; cy: number; r: number }>> = {
  info: { cx: 12, cy: 12, r: 9 },
  eye: { cx: 12, cy: 12, r: 3 },
};

/** Los iconos rellenos no llevan trazo. */
const FILLED: IconName[] = ['pin'];

interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  color?: string;
  className?: string;
}

export function Icon({
  name,
  size = 19,
  strokeWidth = 1.9,
  color = 'currentColor',
  className,
}: IconProps) {
  const filled = FILLED.includes(name);
  const circle = EXTRA_CIRCLE[name];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? color : 'none'}
      stroke={filled ? 'none' : color}
      strokeWidth={filled ? undefined : strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {circle ? <circle cx={circle.cx} cy={circle.cy} r={circle.r} /> : null}
      <path d={PATHS[name]} />
    </svg>
  );
}
